var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { deg2rad, mm2cm, rad2deg } from "../insole-web-common/src/engine/parameters.js";
import { DeepClone } from "../insole-web-common/src/util.js";
import { GetWidthPointPairMedialIndex, GetWidthPointPairLateralIndex } from "../border-width-parameters.js";
import { IsNotNullish } from "../util.js";
import { GetCurrentMetadataVersion } from "./metadata-backwards-compatibility.js";
import { GetInsoleParameter, SetInsoleParameter } from "../parameters.js";
function CreateParameterSetter(targetParams) {
    return (key, value) => {
        if (IsNotNullish(value)) {
            targetParams[key] = value;
        }
    };
}
function MultiplyIfNeeded(value, multiplier) {
    if (IsNotNullish(value)) {
        return value * multiplier;
    }
    else {
        return value;
    }
}
function Deg2Rad(value) {
    return MultiplyIfNeeded(value, deg2rad);
}
function Rad2Deg(value) {
    return value * rad2deg;
}
export function ApplyMetadata(metadata, defaultParameters, isHp) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5;
        // Check version
        const requiredVersion = GetCurrentMetadataVersion();
        if (metadata.version !== requiredVersion) {
            console.error(`Incompatible metadata version, required: ${requiredVersion}, actual: ${metadata.version}`);
            return null;
        }
        // Start with the default parameters, only set values from the metadata if needed
        const target = DeepClone(defaultParameters);
        const insoleParams = target.insoleParams;
        const SetInsoleParam = CreateParameterSetter(insoleParams);
        // Template
        if (metadata.selectedTemplate) {
            target.selectedTemplate = metadata.selectedTemplate;
        }
        if (metadata.selectedTemplateSizeType) {
            target.selectedTemplateSizeType = metadata.selectedTemplateSizeType;
        }
        // Dimensions
        SetInsoleParam("physicalLength", (_a = metadata.dimensions) === null || _a === void 0 ? void 0 : _a.length);
        SetInsoleParam("physicalWidth", (_b = metadata.dimensions) === null || _b === void 0 ? void 0 : _b.width);
        SetInsoleParam("physicalWidthMet", (_c = metadata.dimensions) === null || _c === void 0 ? void 0 : _c.widthMet);
        if (IsNotNullish((_d = metadata.dimensions) === null || _d === void 0 ? void 0 : _d.length)) {
            SetInsoleParam("globalLengthScale", 0);
        }
        if (IsNotNullish((_e = metadata.dimensions) === null || _e === void 0 ? void 0 : _e.width)) {
            SetInsoleParam("globalWidthScale", 0);
        }
        if (!isHp) {
            if (IsNotNullish((_f = metadata.dimensions) === null || _f === void 0 ? void 0 : _f.widthExtension)) {
                insoleParams.borderWidthMedialPoints[GetWidthPointPairMedialIndex()].proximalWidth =
                    metadata.dimensions.widthExtension / 2.0;
                insoleParams.borderWidthLateralPoints[GetWidthPointPairLateralIndex()].proximalWidth =
                    metadata.dimensions.widthExtension / 2.0;
            }
            if (IsNotNullish((_g = metadata.dimensions) === null || _g === void 0 ? void 0 : _g.widthMetExtension)) {
                insoleParams.borderWidthMedialPoints[GetWidthPointPairMedialIndex()].distalWidth =
                    metadata.dimensions.widthMetExtension / 2.0;
                insoleParams.borderWidthLateralPoints[GetWidthPointPairLateralIndex()].distalWidth =
                    metadata.dimensions.widthMetExtension / 2.0;
            }
        }
        // Arch
        const SetMedialArchParameter = CreateParameterSetter(insoleParams.archParameters.medial);
        SetMedialArchParameter("proximalStartOffset", (_h = metadata.arch) === null || _h === void 0 ? void 0 : _h.proximalStartOffset);
        SetMedialArchParameter("proximalApexHeight", (_j = metadata.arch) === null || _j === void 0 ? void 0 : _j.height);
        SetMedialArchParameter("proximalApexPosition", (_k = metadata.arch) === null || _k === void 0 ? void 0 : _k.apexPosition);
        SetMedialArchParameter("distalDecay", (_l = metadata.arch) === null || _l === void 0 ? void 0 : _l.distalDecay);
        SetMedialArchParameter("archShape", (_m = metadata.arch) === null || _m === void 0 ? void 0 : _m.archShape);
        // Lateral arch
        const SetLateralArchParameter = CreateParameterSetter(insoleParams.archParameters.lateral);
        SetLateralArchParameter("proximalStartOffset", (_o = metadata.arch) === null || _o === void 0 ? void 0 : _o.proximalStartOffset);
        SetLateralArchParameter("proximalApexHeight", (_p = metadata.lateralArch) === null || _p === void 0 ? void 0 : _p.height);
        SetLateralArchParameter("proximalApexPosition", (_q = metadata.lateralArch) === null || _q === void 0 ? void 0 : _q.apexPosition);
        SetLateralArchParameter("distalDecay", (_r = metadata.lateralArch) === null || _r === void 0 ? void 0 : _r.distalDecay);
        // Arch reinforcement
        const SetArchReinforcementInsoleParamsForSide = (arMetadata, arInsoleParams) => {
            if (arMetadata) {
                const SetArchReinforcementParameter = CreateParameterSetter(arInsoleParams);
                SetArchReinforcementParameter("enabled", true);
                SetArchReinforcementParameter("thickness", arMetadata.thickness);
                SetArchReinforcementParameter("proximalStretch", arMetadata.proximalStretch);
                SetArchReinforcementParameter("distalStretch", arMetadata.distalStretch);
                SetArchReinforcementParameter("lateralStretch", arMetadata.lateralStretch);
                if (IsNotNullish(arMetadata.type)) {
                    const map = {
                        thick: "Thick",
                        narrow: "Narrow",
                    };
                    SetArchReinforcementParameter("type", map[arMetadata.type]);
                }
            }
        };
        SetArchReinforcementInsoleParamsForSide(metadata.archReinforcement, insoleParams.medialArchReinforcementParameters);
        SetArchReinforcementInsoleParamsForSide(metadata.lateralArchReinforcement, insoleParams.lateralArchReinforcementParameters);
        // Forefoot post
        if (metadata.forefootPost) {
            const { forefootPost } = metadata;
            const SetForefootPostParameter = CreateParameterSetter(insoleParams.forefootPostParameters);
            SetForefootPostParameter("meshEnabled", forefootPost.meshEnabled);
            SetForefootPostParameter("postAngle", Deg2Rad(forefootPost.angle));
            SetForefootPostParameter("balanceToRearfoot", forefootPost.balanceToRearfoot);
            SetForefootPostParameter("extrinsic", forefootPost.extrinsic);
            SetInsoleParameter(target, "forefootPostingRange", forefootPost.effectRange);
        }
        // Shell
        SetInsoleParam("shellThickness", (_s = metadata.shell) === null || _s === void 0 ? void 0 : _s.thickness);
        SetInsoleParam("distalThickness", (_t = metadata.shell) === null || _t === void 0 ? void 0 : _t.distalThickness);
        CreateParameterSetter(target.meshGenParams)("sideRoundness", (_u = metadata.shell) === null || _u === void 0 ? void 0 : _u.sideRoundingRadius);
        target.meshGenParams.useLegacySideSurfaceGeneration = isHp;
        // First ray cutout
        if (metadata.firstRayCutout) {
            const { firstRayCutout } = metadata;
            SetInsoleParam("firstRayCutoutEnabled", true);
            SetInsoleParam("firstRayCutoutAngle", Deg2Rad(firstRayCutout.angle));
            SetInsoleParam("firstRayCutoutInterspace", firstRayCutout.interspace);
            SetInsoleParam("firstRayCutoutRounding", firstRayCutout.rounding);
        }
        const metaCutoutTypeMap = {
            none: "None",
            ray: "Ray",
            mpj: "MPJ",
        };
        // First metatarsal cutout
        if (metadata.firstMetaCutout) {
            const { firstMetaCutout } = metadata;
            if (IsNotNullish(firstMetaCutout.type) && firstMetaCutout.type !== "none") {
                SetInsoleParameter(target, "leoFirstRayCutoutType", metaCutoutTypeMap[firstMetaCutout.type]);
                if (firstMetaCutout.type === "ray") {
                    SetInsoleParameter(target, "firstMetaCutoutRayLength", firstMetaCutout.length);
                    SetInsoleParameter(target, "firstMetaCutoutRayWidth", firstMetaCutout.width);
                }
                else if (firstMetaCutout.type === "mpj") {
                    SetInsoleParameter(target, "firstMetaCutoutMPJLength", firstMetaCutout.length);
                    SetInsoleParameter(target, "firstMetaCutoutMPJWidth", firstMetaCutout.width);
                }
            }
        }
        // Fifth mnetatarsal cutout
        if (metadata.fifthMetaCutout) {
            const { fifthMetaCutout } = metadata;
            if (IsNotNullish(fifthMetaCutout.type) && fifthMetaCutout.type !== "none") {
                SetInsoleParameter(target, "fifthMetatarsalCutoutType", metaCutoutTypeMap[fifthMetaCutout.type]);
                if (fifthMetaCutout.type === "ray") {
                    SetInsoleParameter(target, "fifthMetaCutoutRayLength", fifthMetaCutout.length);
                    SetInsoleParameter(target, "fifthMetaCutoutRayWidth", fifthMetaCutout.width);
                }
                else if (fifthMetaCutout.type === "mpj") {
                    SetInsoleParameter(target, "fifthMetaCutoutMPJLength", fifthMetaCutout.length);
                    SetInsoleParameter(target, "fifthMetaCutoutMPJWidth", fifthMetaCutout.width);
                }
            }
        }
        // Heel post
        if (metadata.heelPost) {
            const { heelPost } = metadata;
            const SetHeelPostParameter = CreateParameterSetter(insoleParams.heelPostParameters);
            if (IsNotNullish(heelPost.extrinsic)) {
                SetHeelPostParameter("extrinsicEnabled", heelPost.extrinsic);
                if (IsNotNullish(heelPost.type)) {
                    const map = {
                        "u-shape": "UShape",
                        full: "Full",
                        "medial-half": "MedialHalf",
                        "lateral-half": "LateralHalf",
                    };
                    SetHeelPostParameter("postType", map[heelPost.type]);
                }
            }
            SetHeelPostParameter("rollAngle", Deg2Rad(heelPost.angle));
            SetHeelPostParameter("heelLift", heelPost.heelLift);
            SetHeelPostParameter("undercut", Deg2Rad(heelPost.undercut));
            SetHeelPostParameter("distalEdgeRatio", heelPost.distalEdgeRatio);
            SetInsoleParameter(target, "heelPostingRange", heelPost.effectRange);
        }
        // Region based width
        if (metadata.regionBasedWidth) {
            const maxNumPointsPerSide = 4;
            const { regionBasedWidth } = metadata;
            function SetPoints(metadataPoints, insoleEnabledKey, insolePointsKey, insoleNumPointsKey) {
                if (metadataPoints === null || metadataPoints === undefined) {
                    SetInsoleParam(insoleEnabledKey, true);
                    return;
                }
                SetInsoleParam(insoleEnabledKey, metadataPoints.length !== 0);
                const points = metadataPoints.slice(0, maxNumPointsPerSide);
                const insolePoints = insoleParams[insolePointsKey];
                for (let i = 0; i < points.length; ++i) {
                    const src = points[i];
                    const dst = insolePoints[i];
                    const SetShapeParameter = CreateParameterSetter(dst);
                    SetShapeParameter("proximalPoint", src.proximalPoint);
                    SetShapeParameter("distalPoint", src.distalPoint);
                    SetShapeParameter("proximalWidth", src.proximalWidth);
                    SetShapeParameter("distalWidth", src.distalWidth);
                    SetShapeParameter("smoothing", src.smoothing);
                    SetShapeParameter("lockDistal", src.lockDistal);
                }
                SetInsoleParam(insoleNumPointsKey, points.length);
            }
            SetPoints(regionBasedWidth.medialPoints, "borderWidthMedialSideEnabled", "borderWidthMedialPoints", "borderWidthNumMedialPointPairs");
            SetPoints(regionBasedWidth.lateralPoints, "borderWidthLateralSideEnabled", "borderWidthLateralPoints", "borderWidthNumLateralPointPairs");
        }
        // Flanges
        function UpdateFlange(flange, flangeKey) {
            if (!flange) {
                return;
            }
            const SetFlangeParameter = CreateParameterSetter(insoleParams[flangeKey]);
            SetFlangeParameter("enabled", true);
            SetFlangeParameter("width", flange.width);
            SetFlangeParameter("height", flange.height);
            SetFlangeParameter("thickness", flange.thickness);
            SetFlangeParameter("interpolationPointPercentage", flange.apexPoint);
        }
        UpdateFlange(metadata.medialFlange, "medialFlangeParameters");
        UpdateFlange(metadata.lateralFlange, "lateralFlangeParameters");
        // Heel seat
        if (metadata.heelSeat) {
            const SetHeelCupParameter = CreateParameterSetter(insoleParams.heelCupParameters);
            const { heelSeat } = metadata;
            SetHeelCupParameter("enabled", true);
            SetHeelCupParameter("seatHeight", heelSeat.height);
            SetHeelCupParameter("medialFalloffLengthRatio", heelSeat.medialLength);
            SetHeelCupParameter("lateralFalloffLengthRatio", heelSeat.lateralLength);
            SetHeelCupParameter("curvatureShape", heelSeat.curvature);
            if (IsNotNullish(heelSeat.cupThickness)) {
                SetHeelCupParameter("cupThicknessEnabled", true);
                SetHeelCupParameter("cupThickness", heelSeat.cupThickness);
            }
            else {
                SetHeelCupParameter("cupThicknessEnabled", false);
            }
        }
        // Heel skive
        if (metadata.kirbySkive) {
            const { kirbySkive } = metadata;
            SetInsoleParam("heelSkiveEnabled", true);
            SetInsoleParam("heelSkiveIsMedial", kirbySkive.isMedial);
            SetInsoleParam("heelSkiveDepth", kirbySkive.depth);
            SetInsoleParam("heelSkiveAngle", Deg2Rad(kirbySkive.angle));
            SetInsoleParam("heelSkiveTilt", Deg2Rad(kirbySkive.tilt));
        }
        // Shapes
        function SetShapeBase(metadataShape, insoleShape) {
            const SetShapeParameter = CreateParameterSetter(insoleShape);
            SetShapeParameter("referencePointU", metadataShape.referencePointU);
            SetShapeParameter("referencePointV", metadataShape.referencePointV);
            SetShapeParameter("diameterX", metadataShape.sizeX);
            SetShapeParameter("diameterY", metadataShape.sizeY);
            SetShapeParameter("rotationDegrees", metadataShape.rotation);
        }
        function SetHeightMapShape(metadataShape, insoleShape) {
            const SetShapeParameter = CreateParameterSetter(insoleShape);
            SetShapeParameter("enabled", true);
            SetShapeParameter("height", metadataShape.height);
            SetShapeParameter("referencePointU", metadataShape.referencePointU);
            SetShapeParameter("referencePointV", metadataShape.referencePointV);
            SetShapeParameter("scaleX", metadataShape.sizeX);
            SetShapeParameter("scaleY", metadataShape.sizeY);
            SetShapeParameter("rotationDegrees", metadataShape.rotation);
        }
        // Cutout
        if (metadata.cutout) {
            const maxNumShapes = 4;
            const map = {
                none: "None",
                ellipse: "Ellipse",
                rectangular: "Rectangular",
                triangle: "Triangle",
                metatarsal: "MetatarsalCutout",
            };
            const { cutout } = metadata;
            SetInsoleParam("cutoutEnabled", true);
            SetInsoleParam("cutoutBevelRadius", cutout.bevelRadius);
            if (cutout.shapes) {
                const shapes = cutout.shapes.slice(0, maxNumShapes);
                for (let i = 0; i < shapes.length; ++i) {
                    const insoleShape = insoleParams.cutoutShapes[i];
                    const metadataShape = shapes[i];
                    SetShapeBase(metadataShape, insoleShape.boundaryShape);
                    CreateParameterSetter(insoleShape.boundaryShape)("shape", map[(_v = metadataShape.shape) !== null && _v !== void 0 ? _v : "none"]);
                }
            }
        }
        // Reduction
        if (metadata.reduction) {
            const { reduction } = metadata;
            const map = {
                heel: "ReductionHeel",
                medialArch: "ReductionMedialArch",
                lateralArch: "ReductionLateralArch",
                metatarsal: "ReductionMetatarsal",
            };
            const maxNumShapes = 4;
            SetInsoleParam("reductionEnabled", true);
            let shapeIndex = 0;
            for (const shapeType of ["heel", "medialArch", "lateralArch", "metatarsal"]) {
                const hasReductionShape = (_w = reduction[shapeType]) !== null && _w !== void 0 ? _w : true;
                insoleParams.reductionShapes[shapeIndex++].boundaryShape.shape = hasReductionShape
                    ? map[shapeType]
                    : "None";
            }
        }
        // Domes and wells
        if (metadata.domesAndWells) {
            const { domesAndWells } = metadata;
            const maxNumShapes = 4;
            SetInsoleParam("domesAndWellsEnabled", true);
            if (domesAndWells.shapes) {
                const shapes = domesAndWells.shapes.slice(0, maxNumShapes);
                for (let i = 0; i < shapes.length; ++i) {
                    const insoleShape = insoleParams.domesAndWellsShapes[i];
                    const metadataShape = shapes[i];
                    SetShapeBase(metadataShape, insoleShape);
                    const SetShapeParameter = CreateParameterSetter(insoleShape);
                    SetShapeParameter("shape", "Ellipse"); // Shapes from metadata are always ellipse
                    SetShapeParameter("height", metadataShape.height);
                    SetShapeParameter("falloffX1", metadataShape.falloff1);
                    SetShapeParameter("falloffX2", metadataShape.falloff2);
                }
            }
        }
        // Metatarsal pad
        if (metadata.metatarsalPad) {
            const { metatarsalPad } = metadata;
            const map = {
                teardrop: "Teardrop",
                fullbar: "FullBar",
            };
            SetHeightMapShape(metatarsalPad, insoleParams.metatarsalPadParameters.heightMapParameters);
            if (IsNotNullish(metatarsalPad.type)) {
                CreateParameterSetter(insoleParams.metatarsalPadParameters)("shapeType", map[metatarsalPad.type]);
            }
        }
        // Fascial groove
        if (metadata.fascialGroove) {
            const { fascialGroove } = metadata;
            SetHeightMapShape(fascialGroove, insoleParams.fascialGrooveParameters);
        }
        // Styloid
        if (metadata.styloid) {
            const { styloid } = metadata;
            SetHeightMapShape(styloid, insoleParams.styloidParameters);
        }
        // Navicular depression
        if (metadata.navicularDepression) {
            const { navicularDepression } = metadata;
            SetHeightMapShape(navicularDepression, insoleParams.navicularDepressionParameters);
        }
        // Sulcus
        if (metadata.sulcus) {
            const { sulcus } = metadata;
            const typeMap = {
                met: "Met",
                sulcus: "Sulcus",
                full: "Full",
                mortons: "Mortons",
                "gait-plate": "GaitPlate",
            };
            const shoeWidthMap = {
                regular: "Regular",
                wide: "Wide",
                "extra-wide": "ExtraWide",
                narrow: "Narrow",
            };
            const rigidityMap = {
                rigid: "Rigid",
                "semi-rigid": "SemiRigid",
                flexible: "Flexible",
            };
            const SetSulcusParameter = CreateParameterSetter(insoleParams.sulcusParameters);
            SetInsoleParam("insoleShape", typeMap[(_x = sulcus.type) !== null && _x !== void 0 ? _x : "sulcus"]);
            if (IsNotNullish(sulcus.shoeWidth)) {
                SetSulcusParameter("shoeWidth", shoeWidthMap[sulcus.shoeWidth]);
            }
            if (IsNotNullish(sulcus.rigidity)) {
                SetSulcusParameter("internalPreset", rigidityMap[sulcus.rigidity]);
            }
            if (metadata.leoSulcus) {
                SetSulcusParameter("mpj1DistalExtension", metadata.leoSulcus.medialLength);
                SetSulcusParameter("mpj5DistalExtension", metadata.leoSulcus.lateralLength);
            }
        }
        // Morton's extension
        if (metadata.mortonsExtension) {
            const { mortonsExtension } = metadata;
            const isReverse = (_y = mortonsExtension.isReverse) !== null && _y !== void 0 ? _y : false;
            const setter = CreateParameterSetter(isReverse ? insoleParams.reverseMortonsParameters : insoleParams.mortonsParameters);
            setter(`enabled`, true);
            setter(`widthPercent`, mortonsExtension.widthPercent);
            setter(`lengthPercent`, mortonsExtension.lengthPercent);
            setter(`thickness`, mortonsExtension.thickness);
            setter(`lateralEdgeAngle`, mortonsExtension.lateralEdgeAngle);
            setter(`medialEdgeAngle`, mortonsExtension.medialEdgeAngle);
            const rigidity = (() => {
                const { rigidity } = mortonsExtension;
                if (!rigidity) {
                    return null;
                }
                switch (rigidity) {
                    case "rigid":
                        return "Rigid";
                    case "semi-rigid":
                        return isReverse ? "Flexible" : "SemiRigid";
                    case "flexible":
                        return "Flexible";
                }
            })();
            if (rigidity !== null) {
                setter("rigidity", rigidity);
            }
        }
        // Gait-Plate
        if (metadata.gaitPlate) {
            SetInsoleParameter(target, "gaitPlateLength", metadata.gaitPlate.length);
            SetInsoleParameter(target, "gaitPlateCurvature", metadata.gaitPlate.curvature);
        }
        // Thick insole
        if (metadata.thickInsole) {
            SetInsoleParameter(target, "thickInsoleEnabled", true);
            SetInsoleParameter(target, "thickInsoleMiddlePosition", metadata.thickInsole.middlePos);
            SetInsoleParameter(target, "thickInsoleHeelUndercut", metadata.thickInsole.heelUnderCut);
            SetInsoleParameter(target, "thickInsoleMedialUndercut", metadata.thickInsole.medialUnderCut);
            SetInsoleParameter(target, "thickInsoleLateralUndercut", metadata.thickInsole.lateralUnderCut);
            SetInsoleParameter(target, "zoningShapesEnabledOnThick", metadata.thickInsole.enableZones);
        }
        // Lattice
        if (metadata.lattice) {
            SetInsoleParameter(target, "latticeEnabled", true);
            SetInsoleParameter(target, "latticeDefaultCellSize", metadata.lattice.cellSize);
            SetInsoleParameter(target, "latticeDefaultInnerBeamThickness", metadata.lattice.innerBeamThickness);
            SetInsoleParameter(target, "latticeDefaultSurfaceBeamThickness", metadata.lattice.surfaceBeamThickness);
            SetInsoleParameter(target, "latticeDefaultRingThickness", metadata.lattice.ringThickness);
            if (IsNotNullish(metadata.lattice.topCoverThickness) && metadata.lattice.topCoverThickness > 0) {
                SetInsoleParameter(target, "latticeDefaultTopCoverEnabled", true);
                SetInsoleParameter(target, "latticeDefaultTopCoverThickness", metadata.lattice.topCoverThickness);
            }
            if (IsNotNullish(metadata.lattice.zones)) {
                SetInsoleParameter(target, "zoningShapesEnabledOnLattice", true);
                const setZone = (zoneMetadata, index) => {
                    if (IsNotNullish(zoneMetadata)) {
                        if (IsNotNullish(zoneMetadata.innerBeamThickness)) {
                            target.latticeEnabledZones[index] = true;
                            insoleParams.latticeParameters.zoneEdgeThickness[index] = zoneMetadata.innerBeamThickness;
                        }
                    }
                };
                setZone(metadata.lattice.zones.heel, 0);
                setZone(metadata.lattice.zones.medialArch, 1);
                setZone(metadata.lattice.zones.lateralArch, 2);
                setZone(metadata.lattice.zones.metatarsal, 3);
            }
        }
        // Emboss
        if (metadata.emboss) {
            const { emboss } = metadata;
            const SetExportParameter = CreateParameterSetter(target.exportParams);
            if (emboss.embossedText) {
                SetExportParameter("embossedText", emboss.embossedText.text);
                SetExportParameter("embossedFontSize", emboss.embossedText.fontSize);
                SetExportParameter("embossedTextPositionUV", {
                    x: (_z = emboss.embossedText.referencePointU) !== null && _z !== void 0 ? _z : defaultParameters.exportParams.embossedTextPositionUV.x,
                    y: (_0 = emboss.embossedText.referencePointV) !== null && _0 !== void 0 ? _0 : defaultParameters.exportParams.embossedTextPositionUV.y,
                });
            }
            SetExportParameter("embossedLogo", emboss.embossedLogo);
            SetExportParameter("embossedDepth", emboss.embossDepth);
            if (emboss.embossedText || emboss.embossedLogo) {
                SetExportParameter("embossedRotationDegrees", (_1 = emboss.embossRotation) !== null && _1 !== void 0 ? _1 : defaultParameters.exportParams.embossedRotationDegrees);
            }
        }
        // HP specific
        if (metadata.hpSpecific && isHp) {
            const { hpSpecific } = metadata;
            // Arch modification
            if (IsNotNullish(hpSpecific.archModification)) {
                const archModification = hpSpecific.archModification;
                target.archModification = archModification;
                // archModification is just a UI state, so it should be already incorporated into arch height (during HP metadata conversion or during UI state changes)
                //SetMedialArchParameter("proximalApexHeight", target.insoleParams.archParameters.medial.proximalApexHeight + archModification);
            }
            // Arch multipoint fitting
            if (hpSpecific.archMultipointFitting) {
                const { archMultipointFitting } = hpSpecific;
                const SetArchMpfParameter = CreateParameterSetter(insoleParams.archParameters);
                SetArchMpfParameter("multipointFittingAdditionalPoints", archMultipointFitting.numSubdivisionPoints);
                const { x: defaultDistal, y: defaultProximal } = defaultParameters.insoleParams.archParameters.multipointFittingRange;
                SetArchMpfParameter("multipointFittingRange", {
                    x: (_2 = archMultipointFitting.distalPoint) !== null && _2 !== void 0 ? _2 : defaultDistal,
                    y: (_3 = archMultipointFitting.proximalPoint) !== null && _3 !== void 0 ? _3 : defaultProximal,
                });
            }
            // Arch wedge
            if (hpSpecific.archWedge) {
                const { archWedge } = hpSpecific;
                const SetArchWedgeParameter = CreateParameterSetter(insoleParams.archParameters);
                SetArchWedgeParameter("wedgeFillHeight", archWedge.height);
                SetArchWedgeParameter("wedgePatternEnabled", archWedge.patternEnabled);
            }
            // Forefoot posting
            if (hpSpecific.advancedForefootPost) {
                const { advancedForefootPost } = hpSpecific;
                const meshTypeMap = {
                    solid: "Solid",
                    hollow: "HollowCenter",
                };
                const SetForefootPostParameter = CreateParameterSetter(insoleParams.forefootPostParameters);
                SetForefootPostParameter("meshEnabled", advancedForefootPost.meshEnabled);
                SetForefootPostParameter("hexagonPatternEnabled", advancedForefootPost.hexagonPatternEnabled);
                SetForefootPostParameter("meshLateralEdgeRatio", advancedForefootPost.lateralEdgeRatio);
                SetForefootPostParameter("meshMedialEdgeRatio", advancedForefootPost.medialEdgeRatio);
                SetForefootPostParameter("meshProximalEdgeRatio", advancedForefootPost.proximalEdgeRatio);
                if (IsNotNullish(advancedForefootPost.meshType)) {
                    SetForefootPostParameter("meshBaseType", meshTypeMap[advancedForefootPost.meshType]);
                }
            }
            // Width modification
            if (IsNotNullish(hpSpecific.widthModification)) {
                const widthModification = hpSpecific.widthModification * mm2cm;
                target.widthModification = widthModification;
                SetInsoleParam("physicalWidth", target.insoleParams.physicalWidth + widthModification);
            }
            // Heel posting
            if (hpSpecific.advancedHeelPost) {
                const { advancedHeelPost } = hpSpecific;
                const map = {
                    general: "Leo",
                    horseshoe: "HorseShoeHP",
                };
                const SetHeelPostParameter = CreateParameterSetter(insoleParams.heelPostParameters);
                if (IsNotNullish(advancedHeelPost.extrinsicPostType)) {
                    SetHeelPostParameter("extrinsicPostMethod", map[advancedHeelPost.extrinsicPostType]);
                }
                SetHeelPostParameter("curvature", Deg2Rad(advancedHeelPost.curvature));
                SetHeelPostParameter("distalPitch", Deg2Rad(advancedHeelPost.distalPitch));
            }
            // Heel seat
            if (hpSpecific.advancedHeelSeat) {
                const { advancedHeelSeat } = hpSpecific;
                const SetHeelSeatParameter = CreateParameterSetter(insoleParams.heelCupParameters);
                SetHeelSeatParameter("falloffEnd", advancedHeelSeat.falloffEnd);
            }
            // Forefoot width
            if (hpSpecific.forefootWidth) {
                const { forefootWidth } = hpSpecific;
                SetInsoleParam("forefootWidthEnabled", true);
                SetInsoleParam("forefootWidthMpj1Offset", forefootWidth.mpj1offset);
                SetInsoleParam("forefootWidthMpj5Offset", forefootWidth.mpj5offset);
            }
            // Sulcus
            if (hpSpecific.advancedSulcus) {
                const { advancedSulcus } = hpSpecific;
                const SetSulcusParameter = CreateParameterSetter(insoleParams.sulcusParameters);
                const paramNames = [
                    "lengthScaleRatio",
                    "widthScaleRatio",
                    "mpj1DistalExtension",
                    "mpj5DistalExtension",
                ];
                for (const param of paramNames) {
                    SetSulcusParameter(param, advancedSulcus[param]);
                }
            }
            // Export
            if (hpSpecific.advancedExportParams) {
                const exportParams = hpSpecific.advancedExportParams;
                const SetExportParameter = CreateParameterSetter(target.exportParams);
                SetExportParameter("embossedFiducial", exportParams.embossedFiducial);
                SetExportParameter("embossedTextOffset", {
                    x: (_4 = exportParams.embossedTextOffsetX) !== null && _4 !== void 0 ? _4 : target.exportParams.embossedTextOffset.x,
                    y: (_5 = exportParams.embossedTextOffsetY) !== null && _5 !== void 0 ? _5 : target.exportParams.embossedTextOffset.y,
                });
                SetExportParameter("hasPlantarPattern", exportParams.plantarPatternEnabled);
            }
        }
        return target;
    });
}
export function ExportMetadata(parameters, isHp) {
    const { insoleParams } = parameters;
    const result = {
        version: GetCurrentMetadataVersion(),
    };
    // Template
    result.selectedTemplate = parameters.selectedTemplate;
    result.selectedTemplateSizeType = parameters.selectedTemplateSizeType;
    // Dimensions
    if (isHp) {
        result.dimensions = {
            length: insoleParams.physicalLength,
            width: insoleParams.physicalWidth,
            widthMet: insoleParams.physicalWidthMet,
            widthExtension: 0.0,
            widthMetExtension: 0.0,
        };
    }
    else {
        result.dimensions = {
            length: insoleParams.physicalLength,
            width: insoleParams.physicalWidth,
            widthMet: insoleParams.physicalWidthMet,
            widthExtension: 2.0 * insoleParams.borderWidthMedialPoints[GetWidthPointPairMedialIndex()].proximalWidth,
            widthMetExtension: 2.0 * insoleParams.borderWidthMedialPoints[GetWidthPointPairMedialIndex()].distalWidth,
        };
    }
    // Arch
    result.arch = {
        proximalStartOffset: insoleParams.archParameters.medial.proximalStartOffset,
        height: insoleParams.archParameters.medial.proximalApexHeight,
        apexPosition: insoleParams.archParameters.medial.proximalApexPosition,
        distalDecay: insoleParams.archParameters.medial.distalDecay,
        archShape: insoleParams.archParameters.medial.archShape,
    };
    // Lateral arch
    result.lateralArch = {
        proximalStartOffset: insoleParams.archParameters.medial.proximalStartOffset,
        height: insoleParams.archParameters.lateral.proximalApexHeight,
        apexPosition: insoleParams.archParameters.lateral.proximalApexPosition,
        distalDecay: insoleParams.archParameters.lateral.distalDecay,
        archShape: insoleParams.archParameters.lateral.archShape,
    };
    // Arch reinforcement
    const SetArchReinforcementMetadataForSide = (arInsoleParams) => {
        if (arInsoleParams.enabled) {
            const map = {
                Thick: "thick",
                Narrow: "narrow",
            };
            return {
                type: map[arInsoleParams.type],
                thickness: arInsoleParams.thickness,
                proximalStretch: arInsoleParams.proximalStretch,
                distalStretch: arInsoleParams.distalStretch,
                lateralStretch: arInsoleParams.lateralStretch,
            };
        }
        return undefined;
    };
    result.archReinforcement = SetArchReinforcementMetadataForSide(insoleParams.medialArchReinforcementParameters);
    result.lateralArchReinforcement = SetArchReinforcementMetadataForSide(insoleParams.lateralArchReinforcementParameters);
    // Forefoot post
    if (insoleParams.forefootPostParameters.meshEnabled || insoleParams.forefootPostParameters.postAngle !== 0) {
        const { forefootPostParameters } = insoleParams;
        result.forefootPost = {
            extrinsic: forefootPostParameters.extrinsic,
            meshEnabled: forefootPostParameters.meshEnabled,
            angle: Rad2Deg(forefootPostParameters.postAngle),
            effectRange: GetInsoleParameter(parameters, "forefootPostingRange"),
            balanceToRearfoot: forefootPostParameters.balanceToRearfoot,
        };
    }
    // Shell
    result.shell = {
        thickness: insoleParams.shellThickness,
        distalThickness: insoleParams.distalThickness,
        distalToShellThicknessTransitionEnd: insoleParams.distalToShellThicknessTransitionEnd,
        sideRoundingRadius: parameters.meshGenParams.sideRoundness,
    };
    // First ray cutout
    if (insoleParams.firstRayCutoutEnabled) {
        result.firstRayCutout = {
            angle: Rad2Deg(insoleParams.firstRayCutoutAngle),
            interspace: insoleParams.firstRayCutoutInterspace,
            rounding: insoleParams.firstRayCutoutRounding,
        };
    }
    const metaCutoutTypeMap = {
        None: "none",
        Ray: "ray",
        MPJ: "mpj",
    };
    // First metatarsal cutout
    if (insoleParams.leoFirstRayCutoutType !== "None") {
        result.firstMetaCutout = {
            type: metaCutoutTypeMap[insoleParams.leoFirstRayCutoutType],
            length: GetInsoleParameter(parameters, insoleParams.leoFirstRayCutoutType === "Ray" ? "firstMetaCutoutRayLength" : "firstMetaCutoutMPJLength"),
            width: GetInsoleParameter(parameters, insoleParams.leoFirstRayCutoutType === "Ray" ? "firstMetaCutoutRayWidth" : "firstMetaCutoutMPJWidth"),
        };
    }
    // Fifth metatarsal cutout
    if (insoleParams.fifthMetatarsalCutoutType !== "None") {
        result.fifthMetaCutout = {
            type: metaCutoutTypeMap[insoleParams.fifthMetatarsalCutoutType],
            length: GetInsoleParameter(parameters, insoleParams.leoFirstRayCutoutType === "Ray" ? "fifthMetaCutoutRayLength" : "fifthMetaCutoutMPJLength"),
            width: GetInsoleParameter(parameters, insoleParams.leoFirstRayCutoutType === "Ray" ? "fifthMetaCutoutRayWidth" : "fifthMetaCutoutMPJWidth"),
        };
    }
    // Heel post
    if (insoleParams.heelPostParameters.extrinsicEnabled || insoleParams.heelPostParameters.intrinsicEnabled) {
        const { heelPostParameters } = insoleParams;
        const map = {
            MedialHalf: "medial-half",
            LateralHalf: "lateral-half",
            Full: "full",
            UShape: "u-shape",
            Custom: "full", // Not available in metadata
        };
        result.heelPost = {
            extrinsic: heelPostParameters.extrinsicEnabled,
            type: map[heelPostParameters.postType],
            angle: Rad2Deg(heelPostParameters.rollAngle),
            heelLift: heelPostParameters.heelLift,
            undercut: Rad2Deg(heelPostParameters.undercut),
            distalEdgeRatio: heelPostParameters.distalEdgeRatio,
            effectRange: GetInsoleParameter(parameters, "heelPostingRange"),
        };
    }
    // Region based width
    if (insoleParams.borderWidthNumMedialPointPairs !== 0 || insoleParams.borderWidthNumLateralPointPairs !== 0) {
        function ProcessPointPairs(pointPairs, numPointPairs) {
            return pointPairs
                .slice(0, numPointPairs)
                .filter(pointPair => pointPair.proximalWidth !== 0 || pointPair.distalWidth !== 0)
                .map(pointPair => ({
                proximalPoint: pointPair.proximalPoint,
                distalPoint: pointPair.distalPoint,
                proximalWidth: pointPair.proximalWidth,
                distalWidth: pointPair.distalWidth,
                smoothing: pointPair.smoothing,
                lockDistal: pointPair.lockDistal,
            }));
        }
        const medialPoints = ProcessPointPairs(insoleParams.borderWidthMedialPoints, insoleParams.borderWidthNumMedialPointPairs);
        const lateralPoints = ProcessPointPairs(insoleParams.borderWidthLateralPoints, insoleParams.borderWidthNumLateralPointPairs);
        if (medialPoints.length !== 0 || lateralPoints.length !== 0) {
            result.regionBasedWidth = {
                medialPoints,
                lateralPoints,
            };
        }
    }
    // Flanges
    function UpdateFlange(key, flangeParameters) {
        if (!flangeParameters.enabled) {
            return;
        }
        result[key] = {
            width: flangeParameters.width,
            height: flangeParameters.height,
            thickness: flangeParameters.thickness,
            apexPoint: flangeParameters.interpolationPointPercentage,
        };
    }
    UpdateFlange("medialFlange", insoleParams.medialFlangeParameters);
    UpdateFlange("lateralFlange", insoleParams.lateralFlangeParameters);
    // Heel seat
    if (insoleParams.heelCupParameters.enabled) {
        result.heelSeat = {
            height: insoleParams.heelCupParameters.seatHeight,
            medialLength: insoleParams.heelCupParameters.medialFalloffLengthRatio,
            lateralLength: insoleParams.heelCupParameters.lateralFalloffLengthRatio,
            curvature: insoleParams.heelCupParameters.curvatureShape,
            cupThickness: insoleParams.heelCupParameters.cupThicknessEnabled
                ? insoleParams.heelCupParameters.cupThickness
                : null,
        };
    }
    // Heel skive
    if (insoleParams.heelSkiveEnabled) {
        result.kirbySkive = {
            isMedial: insoleParams.heelSkiveIsMedial,
            depth: insoleParams.heelSkiveDepth,
            angle: Rad2Deg(insoleParams.heelSkiveAngle),
            tilt: Rad2Deg(insoleParams.heelSkiveTilt),
        };
    }
    // Shapes
    function MapShapeBase(shape) {
        return {
            referencePointU: shape.referencePointU,
            referencePointV: shape.referencePointV,
            sizeX: shape.diameterX,
            sizeY: shape.diameterY,
            rotation: shape.rotationDegrees,
        };
    }
    function MapHeightMapShape(shape) {
        return {
            height: shape.height,
            referencePointU: shape.referencePointU,
            referencePointV: shape.referencePointV,
            sizeX: shape.scaleX,
            sizeY: shape.scaleY,
            rotation: shape.rotationDegrees,
        };
    }
    // Cutout
    const maxNumCutoutShapes = 4;
    let numCutoutShapes = 0;
    for (let i = 0; i < maxNumCutoutShapes; ++i) {
        if (insoleParams.cutoutShapes[i].boundaryShape.shape !== "None") {
            ++numCutoutShapes;
        }
    }
    if (insoleParams.cutoutEnabled && numCutoutShapes > 0) {
        const map = {
            None: null,
            Ellipse: "ellipse",
            Rectangular: "rectangular",
            Triangle: "triangle",
            MetatarsalCutout: "metatarsal",
            FifthMetatarsalCutout: null,
            ReductionHeel: null,
            ReductionMedialArch: null,
            ReductionLateralArch: null,
            ReductionMetatarsal: null,
            Custom: null,
        };
        function MapShape(shape) {
            const { boundaryShape } = shape;
            const mappedShape = map[boundaryShape.shape];
            if (mappedShape === null) {
                return null;
            }
            return Object.assign({ shape: mappedShape }, MapShapeBase(boundaryShape));
        }
        result.cutout = {
            bevelRadius: insoleParams.cutoutBevelRadius,
            shapes: insoleParams.cutoutShapes
                .map(MapShape)
                .filter(shape => shape !== null)
                .map(shape => shape), // Null values are filtered, so the cast is safe
        };
    }
    // Reduction
    const maxNumReductionShapes = 4;
    let numReductionShapes = 0;
    for (let i = 0; i < maxNumReductionShapes; ++i) {
        if (insoleParams.reductionShapes[i].boundaryShape.shape !== "None") {
            ++numReductionShapes;
        }
    }
    if (insoleParams.reductionEnabled && numReductionShapes > 0) {
        const map = {
            None: null,
            Ellipse: null,
            Rectangular: null,
            Triangle: null,
            MetatarsalCutout: null,
            FifthMetatarsalCutout: null,
            ReductionHeel: "heel",
            ReductionMedialArch: "medialArch",
            ReductionLateralArch: "lateralArch",
            ReductionMetatarsal: "metatarsal",
            Custom: null,
        };
        function HasShape(shapeType) {
            const maxNumReductionShapes = 4;
            for (let i = 0; i < maxNumReductionShapes; ++i) {
                const currentShapeType = map[insoleParams.reductionShapes[i].boundaryShape.shape];
                if (currentShapeType === shapeType) {
                    return true;
                }
            }
            return false;
        }
        result.reduction = {
            heel: HasShape("heel"),
            medialArch: HasShape("medialArch"),
            lateralArch: HasShape("lateralArch"),
            metatarsal: HasShape("metatarsal"),
        };
    }
    // Domes and wells
    const maxNumDomesAndWellsShapes = 4;
    let numDomesAndWellsShapes = 0;
    for (let i = 0; i < maxNumDomesAndWellsShapes; ++i) {
        if (insoleParams.domesAndWellsShapes[i].shape !== "None") {
            ++numDomesAndWellsShapes;
        }
    }
    if (insoleParams.domesAndWellsEnabled && numDomesAndWellsShapes > 0) {
        const map = {
            None: null,
            Ellipse: "ellipse",
            Custom: null,
        };
        function MapShape(shape) {
            const mappedShape = map[shape.shape];
            if (mappedShape === null) {
                return null;
            }
            return Object.assign({ height: shape.height, falloff1: shape.falloffX1, falloff2: shape.falloffX2 }, MapShapeBase(shape));
        }
        result.domesAndWells = {
            shapes: insoleParams.domesAndWellsShapes
                .map(MapShape)
                .filter(shape => shape !== null)
                .map(shape => shape), // Null values are filtered, so the cast is safe
        };
    }
    // Metatarsal pad
    if (insoleParams.metatarsalPadParameters.heightMapParameters.enabled) {
        const { metatarsalPadParameters } = insoleParams;
        const map = {
            Teardrop: "teardrop",
            FullBar: "fullbar",
        };
        result.metatarsalPad = Object.assign({ type: map[metatarsalPadParameters.shapeType] }, MapHeightMapShape(metatarsalPadParameters.heightMapParameters));
    }
    // Fascial groove
    if (insoleParams.fascialGrooveParameters.enabled) {
        result.fascialGroove = MapHeightMapShape(insoleParams.fascialGrooveParameters);
    }
    // Styloid
    if (insoleParams.styloidParameters.enabled) {
        result.styloid = MapHeightMapShape(insoleParams.styloidParameters);
    }
    // Navicular depression
    if (insoleParams.navicularDepressionParameters.enabled) {
        result.navicularDepression = MapHeightMapShape(insoleParams.navicularDepressionParameters);
    }
    // Rigidity-map for distal area
    // Use "rigid" for unused types
    const rigidityMap = {
        Rigid: "rigid",
        SemiRigid: "semi-rigid",
        Flexible: "flexible",
        Custom: "rigid",
    };
    // Sulcus
    if (insoleParams.insoleShape !== "Met") {
        const { sulcusParameters } = insoleParams;
        // The type for a met length insole should be "met", but since that cannot happen, "sulcus" is used (because of type checks)
        const typeMap = {
            Met: "met",
            Sulcus: "sulcus",
            Full: "full",
            Mortons: "mortons",
            GaitPlate: "gait-plate",
        };
        const shoeWidthMap = {
            Regular: "regular",
            Wide: "wide",
            ExtraWide: "extra-wide",
            Narrow: "narrow",
        };
        result.sulcus = {
            type: typeMap[insoleParams.insoleShape],
            reversed: insoleParams.sulcusParameters.reverse,
            shoeWidth: shoeWidthMap[sulcusParameters.shoeWidth],
            rigidity: rigidityMap[sulcusParameters.internalPreset],
        };
    }
    // LeoSulcus
    if (insoleParams.insoleShape === "Sulcus") {
        result.leoSulcus = {
            medialLength: insoleParams.sulcusParameters.mpj1DistalExtension,
            lateralLength: insoleParams.sulcusParameters.mpj5DistalExtension,
        };
    }
    // Morton's extension
    const mortonsEnabled = isHp
        ? insoleParams.mortonsParameters.enabled
        : insoleParams.insoleShape === "Mortons" && !insoleParams.sulcusParameters.reverse;
    const reverseMortonsEnabled = isHp
        ? insoleParams.reverseMortonsParameters.enabled
        : insoleParams.insoleShape === "Mortons" && insoleParams.sulcusParameters.reverse;
    if (mortonsEnabled || reverseMortonsEnabled) {
        // TODO: for Leo, most of the fields will be ignored (except width/lengthPercent)
        const prefix = reverseMortonsEnabled ? "reverseMortons" : "mortons";
        result.mortonsExtension = {
            isReverse: reverseMortonsEnabled,
            widthPercent: insoleParams[`${prefix}Parameters`].widthPercent,
            lengthPercent: insoleParams[`${prefix}Parameters`].lengthPercent,
            thickness: isHp ? insoleParams[`${prefix}Parameters`].thickness : insoleParams.distalThickness,
            lateralEdgeAngle: insoleParams[`${prefix}Parameters`].lateralEdgeAngle,
            medialEdgeAngle: insoleParams[`${prefix}Parameters`].medialEdgeAngle,
            rigidity: isHp
                ? rigidityMap[insoleParams[`${prefix}Parameters`].rigidity]
                : rigidityMap[insoleParams.sulcusParameters.internalPreset],
        };
    }
    // Gait-Plate
    if (insoleParams.insoleShape === "GaitPlate") {
        result.gaitPlate = {
            length: insoleParams.gaitPlateParameters.lengthPercent,
            curvature: GetInsoleParameter(parameters, "gaitPlateCurvature"),
        };
    }
    // Thick insole
    if (insoleParams.solidParameters.enabled) {
        result.thickInsole = {
            middlePos: insoleParams.solidParameters.midPoint,
            heelUnderCut: insoleParams.solidParameters.heel,
            medialUnderCut: insoleParams.solidParameters.medial,
            lateralUnderCut: insoleParams.solidParameters.lateral,
            enableZones: GetInsoleParameter(parameters, "zoningShapesEnabledOnThick"),
        };
    }
    // Lattice
    if (GetInsoleParameter(parameters, "latticeEnabled")) {
        const enableZones = GetInsoleParameter(parameters, "zoningShapesEnabledOnLattice") &&
            (parameters.latticeEnabledZones[0] ||
                parameters.latticeEnabledZones[1] ||
                parameters.latticeEnabledZones[2] ||
                parameters.latticeEnabledZones[4]);
        const zones = enableZones
            ? {
                heel: parameters.latticeEnabledZones[0]
                    ? {
                        innerBeamThickness: insoleParams.latticeParameters.zoneEdgeThickness[0],
                        surfaceBeamThickness: insoleParams.latticeParameters.zoneBeamThickness[0],
                    }
                    : null,
                medialArch: parameters.latticeEnabledZones[1]
                    ? {
                        innerBeamThickness: insoleParams.latticeParameters.zoneEdgeThickness[1],
                        surfaceBeamThickness: insoleParams.latticeParameters.zoneBeamThickness[1],
                    }
                    : null,
                lateralArch: parameters.latticeEnabledZones[2]
                    ? {
                        innerBeamThickness: insoleParams.latticeParameters.zoneEdgeThickness[2],
                        surfaceBeamThickness: insoleParams.latticeParameters.zoneBeamThickness[2],
                    }
                    : null,
                metatarsal: parameters.latticeEnabledZones[3]
                    ? {
                        innerBeamThickness: insoleParams.latticeParameters.zoneEdgeThickness[3],
                        surfaceBeamThickness: insoleParams.latticeParameters.zoneBeamThickness[3],
                    }
                    : null,
            }
            : null;
        result.lattice = {
            cellSize: insoleParams.latticeParameters.cellSize,
            innerBeamThickness: insoleParams.latticeParameters.edgeThickness,
            surfaceBeamThickness: insoleParams.latticeParameters.beamThickness,
            ringThickness: insoleParams.latticeParameters.ringThickness,
            topCoverThickness: insoleParams.latticeParameters.topCoverEnabled
                ? insoleParams.latticeParameters.topCoverThickness
                : 0.0,
            zones,
        };
    }
    // Emboss
    if (parameters.exportParams.embossedLogo || parameters.exportParams.embossedText.length !== 0) {
        const { exportParams } = parameters;
        result.emboss = {
            embossedText: {
                text: exportParams.embossedText.length !== 0 && typeof exportParams.embossedText === "string"
                    ? exportParams.embossedText
                    : "",
                fontSize: exportParams.embossedFontSize,
                referencePointU: exportParams.embossedTextPositionUV.x,
                referencePointV: exportParams.embossedTextPositionUV.y,
            },
            embossedLogo: exportParams.embossedLogo,
            embossRotation: exportParams.embossedRotationDegrees,
            embossDepth: exportParams.embossedDepth,
        };
    }
    // HP specific
    if (isHp) {
        function GetHPSpecific() {
            var _a;
            return ((_a = result.hpSpecific) !== null && _a !== void 0 ? _a : (result.hpSpecific = {}));
        }
        if (parameters.archModification !== 0) {
            GetHPSpecific().archModification = parameters.archModification;
        }
        // Arch multipoint fitting
        if (insoleParams.archParameters.multipointFittingAdditionalPoints !== 0) {
            const archMpf = insoleParams.archParameters;
            GetHPSpecific().archMultipointFitting = {
                numSubdivisionPoints: archMpf.multipointFittingAdditionalPoints,
                distalPoint: archMpf.multipointFittingRange.x,
                proximalPoint: archMpf.multipointFittingRange.y,
            };
        }
        // Arch wedge
        if (insoleParams.archParameters.wedgeFillHeight !== 0) {
            const archWedge = insoleParams.archParameters;
            GetHPSpecific().archWedge = {
                height: archWedge.wedgeFillHeight,
                patternEnabled: archWedge.wedgePatternEnabled,
            };
        }
        // Forefoot posting
        if (insoleParams.forefootPostParameters.meshEnabled) {
            const meshTypeMap = {
                Solid: "solid",
                HollowCenter: "hollow",
                HollowProximal: "hollow",
                HollowDistal: "hollow",
                HollowMedial: "hollow",
                HollowLateral: "hollow",
            };
            const afp = insoleParams.forefootPostParameters;
            GetHPSpecific().advancedForefootPost = {
                meshEnabled: true,
                hexagonPatternEnabled: afp.hexagonPatternEnabled,
                lateralEdgeRatio: afp.meshLateralEdgeRatio,
                medialEdgeRatio: afp.meshMedialEdgeRatio,
                proximalEdgeRatio: afp.meshProximalEdgeRatio,
                meshType: meshTypeMap[afp.meshBaseType],
            };
        }
        // Width modification
        if (parameters.widthModification !== 0) {
            GetHPSpecific().widthModification = parameters.widthModification;
        }
        // Heel posting
        if (insoleParams.heelPostParameters.extrinsicEnabled) {
            const { heelPostParameters } = insoleParams;
            const map = {
                Leo: "general",
                HorseShoeHP: "horseshoe",
            };
            GetHPSpecific().advancedHeelPost = {
                extrinsicPostType: map[heelPostParameters.extrinsicPostMethod],
                curvature: Rad2Deg(heelPostParameters.curvature),
                distalPitch: Rad2Deg(heelPostParameters.distalPitch),
            };
        }
        // Heel seat
        if (insoleParams.heelCupParameters.falloffEnd !== 1) {
            GetHPSpecific().advancedHeelSeat = {
                falloffEnd: insoleParams.heelCupParameters.falloffEnd,
            };
        }
        // Forefoot width
        if (insoleParams.forefootWidthEnabled) {
            GetHPSpecific().forefootWidth = {
                mpj1offset: insoleParams.forefootWidthMpj1Offset,
                mpj5offset: insoleParams.forefootWidthMpj5Offset,
            };
        }
        // Sulcus
        if (insoleParams.insoleShape !== "Met") {
            const { sulcusParameters } = insoleParams;
            GetHPSpecific().advancedSulcus = {
                lengthScaleRatio: sulcusParameters.lengthScaleRatio,
                widthScaleRatio: sulcusParameters.widthScaleRatio,
                mpj1DistalExtension: sulcusParameters.mpj1DistalExtension,
                mpj5DistalExtension: sulcusParameters.mpj5DistalExtension,
            };
        }
        // Export
        GetHPSpecific().advancedExportParams = {
            embossedFiducial: parameters.exportParams.embossedFiducial,
            embossedTextOffsetX: parameters.exportParams.embossedTextOffset.x,
            embossedTextOffsetY: parameters.exportParams.embossedTextOffset.y,
            plantarPatternEnabled: parameters.exportParams.hasPlantarPattern,
        };
    }
    return result;
}
//# sourceMappingURL=metadata.js.map